import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { RetailWidgetKey } from '@/hooks/useDashboardPreferences';

export interface RetailMetricOption {
  key: RetailWidgetKey;
  label: string;
  description: string;
}

// Exact metrics and descriptions from Lightspeed Retail, in their exact 2-column layout order
export const RETAIL_METRIC_OPTIONS: RetailMetricOption[] = [
  // Left column entries (indices 0, 2, 4, 6, 8)
  { key: 'revenue', label: 'Revenue', description: 'Get a snapshot of your overall revenue by outlet(s)' },
  { key: 'gross_profit', label: 'Gross profit', description: 'Keep an eye on your margins' },
  { key: 'sale_count', label: 'Sale count', description: 'Watch how many sales are being made in-store' },
  { key: 'sales_with_customer_details', label: 'Sales with customer details', description: 'Track the percentage of sales that have customer details recorded' },
  { key: 'avg_items_per_sale', label: 'Avg. items per sale', description: 'See the average number of items sold per sale' },
  // Right column entries (indices 1, 3, 5, 7, 9)
  { key: 'avg_sale_value', label: 'Avg. sale value', description: 'See the average revenue made per sale' },
  { key: 'discounted', label: 'Discounted', description: 'Monitor the discounts applied by staff' },
  { key: 'my_sales_performance', label: 'My sales performance', description: 'Keep track of your own sales performance' },
  { key: 'top_products_sold', label: 'Top products sold', description: 'Easily view your bestselling products' },
  { key: 'top_sales_people', label: 'Top sales people', description: 'Easily view your best-performing sales people' },
];

// Layout: left column = [0,1,2,3,4], right column = [5,6,7,8,9]
// to match the Lightspeed 2-column layout exactly
const LEFT_COLUMN: RetailWidgetKey[] = [
  'revenue',
  'gross_profit',
  'sale_count',
  'sales_with_customer_details',
  'avg_items_per_sale',
];
const RIGHT_COLUMN: RetailWidgetKey[] = [
  'avg_sale_value',
  'discounted',
  'my_sales_performance',
  'top_products_sold',
  'top_sales_people',
];

interface RetailMetricsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enabledWidgets: RetailWidgetKey[];
  onSave: (widgets: RetailWidgetKey[]) => void;
}

export function RetailMetricsModal({
  open,
  onOpenChange,
  enabledWidgets,
  onSave,
}: RetailMetricsModalProps) {
  // Local state for checkbox changes (only persisted on "Update dashboard")
  const [localEnabled, setLocalEnabled] = useState<RetailWidgetKey[]>(enabledWidgets);

  // Reset local state when modal opens
  useEffect(() => {
    if (open) {
      setLocalEnabled(enabledWidgets);
    }
  }, [open, enabledWidgets]);

  const toggleWidget = (key: RetailWidgetKey) => {
    setLocalEnabled(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSave = () => {
    onSave(localEnabled);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const getOption = (key: RetailWidgetKey) =>
    RETAIL_METRIC_OPTIONS.find(o => o.key === key)!;

  const renderCheckboxRow = (key: RetailWidgetKey) => {
    const option = getOption(key);
    const checked = localEnabled.includes(key);
    return (
      <label
        key={key}
        className="flex items-start gap-3 cursor-pointer py-3"
      >
        <Checkbox
          checked={checked}
          onCheckedChange={() => toggleWidget(key)}
          className="mt-0.5 h-5 w-5 rounded border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">{option.label}</p>
          <p className="text-sm text-muted-foreground leading-snug mt-0.5">
            {option.description}
          </p>
        </div>
      </label>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Show more Retail metrics
          </DialogTitle>
          <DialogDescription>
            Choose the information you want to see on your Home dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0 py-2">
          {/* Left column */}
          <div className="flex flex-col divide-y">
            {LEFT_COLUMN.map(key => renderCheckboxRow(key))}
          </div>
          {/* Right column */}
          <div className="flex flex-col divide-y">
            {RIGHT_COLUMN.map(key => renderCheckboxRow(key))}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Update dashboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
